import { useState } from "react";
import {
  Box,
  Grid,
  Image,
  Button,
  Heading,
  VStack,
  Container,
  Text,
  Flex,
  useBreakpointValue,
  keyframes,
  ScaleFade,
  HStack,
  Badge,
} from "@chakra-ui/react";

const glowAnimation = keyframes`
  0% { box-shadow: 0 0 15px rgba(0, 163, 255, 0.4); }
  50% { box-shadow: 0 0 25px rgba(0, 163, 255, 0.6); }
  100% { box-shadow: 0 0 15px rgba(0, 163, 255, 0.4); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

export const GridPromoKlMAR = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const columns = useBreakpointValue({ base: 1, md: 3, lg: 3 });
  const imageHeight = useBreakpointValue({ base: "200px", lg: "400px" });
  const headingSize = useBreakpointValue({ base: "2xl", lg: "3xl" });

  const destinations = [
    {
      title: "LIMA",
      code: "LIM",
      price: "From €1123",
      thumbnail: "https://travel-time.ro/images/af-kl/kl-mar-2026/lima.jpg",
    },
    {
      title: "CAPE TOWN",
      code: "CPT",
      price: "From €752",
      thumbnail:
        "https://travel-time.ro/images/af-kl/kl-mar-2026/cape-town.jpg",
    },
    {
      title: "VANCOUVER",
      code: "YVR",
      price: "From €947",
      thumbnail:
        "https://travel-time.ro/images/af-kl/kl-mar-2026/vancouver.jpg",
    },
  ];

  const getBadgeColor = (badge) => {
    const colors = {
      Popular: "purple",
      Limited: "red",
      "Hot Deal": "orange",
      New: "green",
      Special: "blue",
      "Best Price": "yellow",
      Featured: "cyan",
      Trending: "pink",
    };
    return colors[badge] || "gray";
  };

  return (
    <Box
      bgGradient="linear(135deg, #ffffff 0%, rgb(255, 255, 255) 100%)"
      minH="100vh"
      py={12}
      px={4}
      position="relative"
    >
      {/* Background overlay pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.1}
        backgroundImage="radial-gradient(circle at 25% 25%, white 2px, transparent 2px)"
        backgroundSize="50px 50px"
      />

      <Container maxW="container.2xl" position="relative" zIndex={1}>
        {/* Header Section */}
        <VStack spacing={8} mb={16}>
          <Box
            w={{ base: "100%", md: "50%" }}
            rounded="2xl"
            overflow="hidden"
            // shadow="2xl"
            border="2px solid rgba(255, 255, 255, 0.2)"
          >
            <Image
              src="/images/af-kl/KLM-logo.png"
              alt="Air France Logo"
              mx="auto"
              objectFit="contain"
            />
          </Box>

          <VStack spacing={4}>
            <Heading
              size={headingSize}
              color="black"
              textAlign="center"
              fontWeight="bold"
              textShadow="0 2px 4px rgba(0,0,0,0.3)"
            >
              Book your KLM flight now
            </Heading>
            {/* <Text
              color="whiteAlpha.800"
              fontSize="lg"
              textAlign="center"
              maxW="md"
            >
              Ready to create unforgettable memories in the world’s best
              destinations?
            </Text> */}
          </VStack>

          <Box
            h="3px"
            w="300px"
            bgGradient="linear(to-r, cyan.300, blue.400, blue.400)"
            rounded="full"
            shadow="lg"
          />
        </VStack>

        {/* Grid Section */}
        <Grid templateColumns={`repeat(${columns}, 1fr)`} gap={8}>
          {destinations.map((destination, index) => (
            <Box
              key={destination.title}
              position="relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              transform={
                hoveredIndex === index ? "translateY(-8px)" : "translateY(0)"
              }
            >
              <Box
                bg="rgba(255, 255, 255, 0.1)"
                backdropFilter="blur(15px)"
                border="1px solid rgba(255, 255, 255, 0.2)"
                rounded="2xl"
                overflow="hidden"
                position="relative"
                shadow="xl"
                animation={
                  hoveredIndex === index
                    ? `${glowAnimation} 2s infinite`
                    : "none"
                }
                transition="all 0.4s ease"
              >
                {/* Badge */}
                <Badge
                  position="absolute"
                  top={3}
                  right={3}
                  colorScheme={getBadgeColor(destination.badge)}
                  px={3}
                  py={1}
                  rounded="full"
                  fontSize="xs"
                  fontWeight="bold"
                  zIndex={3}
                  animation={`${pulseAnimation} 2s infinite`}
                >
                  {destination.badge}
                </Badge>

                {/* Title */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  p={4}
                  bgGradient="linear(to-b, blackAlpha.700, transparent)"
                  zIndex={2}
                >
                  <VStack align="start" spacing={0}>
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="white"
                      textShadow="0 1px 2px rgba(0,0,0,0.5)"
                    >
                      {destination.title}
                    </Text>
                    <Text
                      fontSize="sm"
                      color="whiteAlpha.800"
                      fontWeight="medium"
                    >
                      {destination.code}
                    </Text>
                  </VStack>
                </Box>

                {/* Image */}
                <Box h={imageHeight} overflow="hidden" position="relative">
                  <Image
                    src={destination.thumbnail}
                    alt={destination.title}
                    w="full"
                    h="full"
                    objectFit="cover"
                    transition="transform 0.6s ease"
                    transform={
                      hoveredIndex === index ? "scale(1.15)" : "scale(1)"
                    }
                  />
                  {/* Image overlay */}
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    h="50%"
                    bgGradient="linear(to-t, blackAlpha.600, transparent)"
                  />
                </Box>

                {/* Price and Buttons Section */}
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  p={4}
                  bgGradient="linear(to-t, blackAlpha.800, blackAlpha.400)"
                >
                  <VStack spacing={3} align="stretch">
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color="white"
                      textAlign="center"
                      textShadow="0 1px 2px rgba(0,0,0,0.5)"
                    >
                      {destination.price}
                    </Text>

                    <HStack spacing={2} justify="center">
                      <Button
                        as="a"
                        href="https://tbs.flytime.ro/reseller/auth/"
                        size="sm"
                        colorScheme="blue"
                        variant="solid"
                        rounded="full"
                        px={4}
                        fontWeight="bold"
                        transition="all 0.3s"
                        _hover={{
                          transform: "scale(1.05)",
                          shadow: "lg",
                        }}
                        shadow="md"
                      >
                        Book Now
                      </Button>

                      {/* <Button
                        as="a"
                        href="https://tbs.flytime.ro/reseller/auth/"
                        size="sm"
                        variant="outline"
                        borderColor="white"
                        color="white"
                        rounded="full"
                        px={4}
                        fontWeight="bold"
                        transition="all 0.3s"
                        _hover={{
                          bg: "whiteAlpha.200",
                          transform: "scale(1.05)",
                          shadow: "lg",
                        }}
                        shadow="md"
                      >
                        Details
                      </Button> */}
                    </HStack>
                  </VStack>
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>

        {/* Footer CTA */}
        <Box mt={16} textAlign="center">
          <VStack spacing={4}>
            <Text color="whiteAlpha.900" fontSize="lg" fontWeight="medium">
              Ready to explore the world?
            </Text>
            <Button
              as="a"
              href="https://tbs.flytime.ro/reseller/auth/"
              size="lg"
              colorScheme="cyan"
              rounded="full"
              px={8}
              py={6}
              fontSize="lg"
              fontWeight="bold"
              shadow="2xl"
              transition="all 0.4s"
              _hover={{
                transform: "translateY(-2px) scale(1.05)",
                shadow: "3xl",
              }}
            >
              View All Destinations
            </Button>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default GridPromoKlMAR;
